import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import CreateExternalUserForm from '../../Components/CreateExternalUserForm';
import TenantPickerModal from '../Tenants/TenantPickerModal';
import { ExternalUser } from '../../types/guestAccess';
import { DATE_ONLY_RE } from '../../Helpers/leaseUtils';
import {
    effectiveDateRange,
    eventTakesEffectiveDate,
    eventTakesReceipt,
    isAcceptedReceiptFile,
    isDestructiveEvent,
} from '../../Helpers/keyRegisterUtils';
import {
    KeyEventInput,
    KeyHolderKind,
    KeyType,
    KEY_EVENT_ACTION_LABELS,
    KEY_HOLDER_KINDS,
    KEY_NOTE_MAX_LENGTH,
    RecordableKeyEventType,
    RegisterKey,
} from '../../types/keyRegister';
import KeyHolderPicker from './KeyHolderPicker';
import { keySerialLabel } from './keyRegisterLabels';

interface KeyEventModalProps {
    show: boolean;
    onHide: () => void;
    registerKey: RegisterKey;
    type: KeyType;
    eventType: RecordableKeyEventType;
    /** YYYY-MM-DD (todayStockholm): the preset and latest allowed date. */
    today: string;
    /** The current holder's name, for the return and loss texts. */
    currentHolderName: string;
    onSubmit: (input: KeyEventInput) => Promise<void>;
    submitting: boolean;
}

interface KeyEventFormValues {
    holderKind: KeyHolderKind;
    holderKey: string;
    effectiveDate: string;
    dueDate: string;
    receiptFile: File | null;
    note: string;
}

const buildSchema = (eventType: RecordableKeyEventType, range: { min?: string; max: string }) => Yup.object({
    holderKind: Yup.mixed<KeyHolderKind>().oneOf(KEY_HOLDER_KINDS).required(),
    holderKey: eventType === 'handedOut' ? Yup.string().required('Välj mottagare') : Yup.string(),
    effectiveDate: eventTakesEffectiveDate(eventType)
        ? Yup.string()
            .required('Välj datum')
            .matches(DATE_ONLY_RE, 'Ogiltigt datum')
            .test('not-future', 'Datumet kan inte vara i framtiden', value => !value || value <= range.max)
            .test('after-previous', `Datumet kan inte vara före nyckelns förra händelse (${range.min})`, value => !value || !range.min || value >= range.min)
        : Yup.string(),
    dueDate: Yup.string()
        .matches(DATE_ONLY_RE, { message: 'Ogiltigt datum', excludeEmptyString: true })
        .test('after-hand-out', 'Kan inte vara före utlämningsdatumet', function (value) {
            const handedOut: string | undefined = this.parent.effectiveDate;
            return !value || !handedOut || value >= handedOut;
        }),
    receiptFile: Yup.mixed<File>()
        .nullable()
        .test('receipt-type', 'Kvittot måste vara en bild eller en PDF', file => !file || isAcceptedReceiptFile(file)),
    note: eventType === 'comment'
        ? Yup.string().trim().required('Skriv en kommentar').max(KEY_NOTE_MAX_LENGTH, `Max ${KEY_NOTE_MAX_LENGTH} tecken`)
        : Yup.string().trim().max(KEY_NOTE_MAX_LENGTH, `Max ${KEY_NOTE_MAX_LENGTH} tecken`),
});

/** What happens, in one sentence; empty for the hand-out and the comment, which are forms of their own. */
const describeEvent = (eventType: RecordableKeyEventType, type: KeyType, holderName: string, hasHolder: boolean): string => {
    switch (eventType) {
        case 'returned':
            return `Nyckeln återlämnas${hasHolder ? ` av ${holderName}` : ''} och får status Inne.`;
        case 'lost':
            return type.kind === 'mechanical'
                ? 'Nyckeln markeras som försvunnen. Mekaniska nycklar kan inte spärras; överväg cylinderbyte.'
                : 'Taggen markeras som försvunnen. Spärra den i passersystemet och registrera spärren här.';
        case 'found':
            return 'Nyckeln markeras som återfunnen och får status Inne.';
        case 'blocked':
            return 'Taggen markeras som spärrad. Själva spärren görs i passersystemet.';
        case 'unblocked':
            return 'Spärren hävs och taggen får status Inne.';
        case 'scrapped':
            return 'Nyckeln kasseras. Det är slutgiltigt och kan inte ångras.';
        case 'handedOut':
        case 'comment':
            return '';
        default: {
            const unhandled: never = eventType;
            return unhandled;
        }
    }
};

type Step = 'form' | 'create-external';

/**
 * Records one event on a key. Hand-out: holder (tenant via TenantPickerModal, app user, external user
 * via ExternalUserSelector), date (today or earlier), optional return date, receipt scan and note.
 * Return: date, receipt and note.
 * Comment: just the required text. Others: a sentence of what happens, note, confirm.
 */
const KeyEventModal: React.FC<KeyEventModalProps> = ({
    show, onHide, registerKey, type, eventType, today, currentHolderName, onSubmit, submitting,
}) => {
    const [step, setStep] = useState<Step>('form');
    const [showTenantPicker, setShowTenantPicker] = useState(false);
    const [externalUser, setExternalUser] = useState<ExternalUser | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const range = useMemo(() => effectiveDateRange(registerKey, today), [registerKey, today]);
    const schema = useMemo(() => buildSchema(eventType, range), [eventType, range]);

    const close = () => {
        setStep('form');
        setExternalUser(null);
        setShowTenantPicker(false);
        onHide();
    };

    const handleSubmit = async (values: KeyEventFormValues, helpers: FormikHelpers<KeyEventFormValues>) => {
        helpers.setStatus(undefined);
        const input: KeyEventInput = {
            type: eventType,
            note: values.note.trim() || undefined,
            ...(eventTakesEffectiveDate(eventType) ? { effectiveDate: values.effectiveDate } : {}),
            ...(eventType === 'handedOut' ? {
                holder: { kind: values.holderKind, key: values.holderKey },
                dueDate: values.dueDate || undefined,
            } : {}),
            ...(eventTakesReceipt(eventType) && values.receiptFile ? { receiptFile: values.receiptFile, receiptKind: 'scan' as const } : {}),
        };
        try {
            await onSubmit(input);
            setExternalUser(null);
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte spara händelsen');
        }
    };

    const title = `${KEY_EVENT_ACTION_LABELS[eventType]}: ${type.name}, ${keySerialLabel(registerKey).toLowerCase()}`;

    return (
        <Modal show={show} onHide={close} centered>
            <Modal.Header closeButton>
                <Modal.Title>{step === 'create-external' ? 'Ny extern användare' : title}</Modal.Title>
            </Modal.Header>
            <Formik<KeyEventFormValues>
                initialValues={{ holderKind: 'tenant', holderKey: '', effectiveDate: today, dueDate: '', receiptFile: null, note: '' }}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, status, handleChange, handleBlur, handleSubmit: submitForm, setStatus, setFieldValue, setFieldTouched }) => {
                    const pickHolder = (key: string) => {
                        void setFieldValue('holderKey', key);
                        void setFieldTouched('holderKey', false, false);
                    };
                    const switchKind = (kind: KeyHolderKind) => {
                        void setFieldValue('holderKind', kind);
                        pickHolder('');
                        setExternalUser(null);
                    };
                    const holderError = touched.holderKey && errors.holderKey;
                    const effectiveDateField = eventTakesEffectiveDate(eventType) && (
                        <Form.Group className="mb-3" controlId="key-event-effective-date">
                            <Form.Label>{eventType === 'handedOut' ? 'Utlämnad den' : 'Återlämnad den'}</Form.Label>
                            <Form.Control
                                type="date"
                                name="effectiveDate"
                                value={values.effectiveDate}
                                min={range.min}
                                max={range.max}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.effectiveDate && !!errors.effectiveDate}
                            />
                            <Form.Control.Feedback type="invalid">{errors.effectiveDate}</Form.Control.Feedback>
                            <Form.Text className="text-muted">Ändra om {eventType === 'handedOut' ? 'nyckeln lämnades ut' : 'nyckeln lämnades tillbaka'} en tidigare dag.</Form.Text>
                        </Form.Group>
                    );

                    if (step === 'create-external') {
                        return (
                            <CreateExternalUserForm
                                onUserCreated={user => {
                                    setExternalUser(user);
                                    pickHolder(user.uid);
                                    setStep('form');
                                }}
                                onCancel={() => setStep('form')}
                            />
                        );
                    }

                    return (
                        <>
                            <Form noValidate onSubmit={submitForm}>
                                <Modal.Body>
                                    {status && <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>}

                                    {eventType === 'comment' ? null : eventType === 'handedOut' ? (
                                        <>
                                            <KeyHolderPicker
                                                kind={values.holderKind}
                                                holderKey={values.holderKey}
                                                error={holderError || undefined}
                                                externalUser={externalUser}
                                                onKindChange={switchKind}
                                                onPick={pickHolder}
                                                onPickExternal={user => {
                                                    setExternalUser(user);
                                                    pickHolder(user.uid);
                                                }}
                                                onCreateExternal={() => setStep('create-external')}
                                                onChooseTenant={() => setShowTenantPicker(true)}
                                            />

                                            {effectiveDateField}

                                            <Form.Group className="mb-3" controlId="key-event-due-date">
                                                <Form.Label>Åter senast</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="dueDate"
                                                    value={values.dueDate}
                                                    min={values.effectiveDate || undefined}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={touched.dueDate && !!errors.dueDate}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.dueDate}</Form.Control.Feedback>
                                                <Form.Text className="text-muted">Valfritt. Med datum blir nyckeln utlånad och kan bli försenad.</Form.Text>
                                            </Form.Group>
                                        </>
                                    ) : (
                                        <>
                                            <p className="mb-3">{describeEvent(eventType, type, currentHolderName, !!registerKey.holder)}</p>
                                            {effectiveDateField}
                                        </>
                                    )}

                                    {eventTakesReceipt(eventType) && (
                                        <Form.Group className="mb-3" controlId="key-event-receipt">
                                            <Form.Label>Kvitto</Form.Label>
                                            <Form.Control
                                                key={fileInputKey}
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={e => {
                                                    const file = (e.target as HTMLInputElement).files?.[0] ?? null;
                                                    void setFieldValue('receiptFile', file);
                                                    void setFieldTouched('receiptFile', true, false);
                                                }}
                                                isInvalid={!!errors.receiptFile}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.receiptFile as string}</Form.Control.Feedback>
                                            {values.receiptFile && (
                                                <Button variant="link" size="sm" className="px-0" onClick={() => {
                                                    void setFieldValue('receiptFile', null);
                                                    setFileInputKey(k => k + 1);
                                                }}>
                                                    Ta bort vald fil
                                                </Button>
                                            )}
                                            <Form.Text className="text-muted d-block">Valfritt. Inskannat kvitto som bild eller PDF.</Form.Text>
                                        </Form.Group>
                                    )}

                                    <Form.Group controlId="key-event-note">
                                        <Form.Label>{eventType === 'comment' ? 'Kommentar *' : 'Anteckning'}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={eventType === 'comment' ? 4 : 2}
                                            name="note"
                                            value={values.note}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            isInvalid={touched.note && !!errors.note}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.note}</Form.Control.Feedback>
                                    </Form.Group>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={close} disabled={submitting}>Avbryt</Button>
                                    <Button variant={isDestructiveEvent(eventType) ? 'danger' : 'primary'} type="submit" disabled={submitting}>
                                        {submitting && <Spinner animation="border" size="sm" className="me-2" />}
                                        {eventType === 'comment' ? 'Spara' : KEY_EVENT_ACTION_LABELS[eventType]}
                                    </Button>
                                </Modal.Footer>
                            </Form>
                            {/* Outside <Form>: React bubbles the picker's own submit through the portal to the enclosing form. */}
                            <TenantPickerModal
                                show={showTenantPicker}
                                onHide={() => setShowTenantPicker(false)}
                                title="Välj mottagare"
                                onPick={tenant => pickHolder(tenant.key)}
                            />
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default KeyEventModal;
